import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Text } from '@kula-ai/components';
import { Paperclip, Send01, XClose } from '@kula-ai/icons-core';
import { MentionEditor, type MentionEditorHandle } from './MentionEditor';

const DISCLAIMER = 'Kula AI can make mistakes. Please double-check responses.';
// Max bytes per file accepted by the picker. Keeps the prompt-prefix path
// honest about what we'll inline. Lift this once a real upload endpoint
// lands and large files can stream up.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

interface ComposerProps {
  placeholder?: string;
  /**
   * `false` (default) — single-line editor with attach + send embedded
   * to the right. Used by the floating panel and fullscreen.
   * `true` — multi-line editor with attach + send pinned beneath. Used
   * by the route-level empty state.
   */
  multiline?: boolean;
  /**
   * When `true`, the previous turn is still resolving — paperclip + send
   * are disabled and Enter no-ops, but the input itself stays editable so
   * the user can keep drafting their follow-up while the agent thinks
   * (matches ChatGPT/Claude). Text already typed is preserved.
   */
  isAwaiting?: boolean;
  /**
   * Called when the user submits non-whitespace input. Composer clears
   * its own editor before invoking, so the caller never sees the stale
   * text. The string is the server-format payload — mentions appear as
   * `<tag id="X" entity="Y">name</tag>` markers in the otherwise-plain
   * text. Attached files are passed as the second argument so the caller can
   * upload them (multipart) rather than smuggling their names into the text.
   */
  onSubmit?: (_text: string, _files?: File[]) => void;
  /**
   * Focus the underlying editor on mount. Mount-only — flipping this
   * later does not re-focus, and successful sends don't refocus either
   * (the editor never blurred). Used by ThreadContainer's Footer so
   * opening a chat thread lands the cursor in the composer.
   */
  autoFocus?: boolean;
  /**
   * Pass-through key handler invoked before Composer's internal
   * Enter / Shift+Enter logic. Call `event.preventDefault()` to
   * suppress the built-in submit-on-Enter for that keystroke.
   */
  onKeyType?: (_event: KeyboardEvent<HTMLElement>) => void;
}

interface ActionsProps {
  canSend: boolean;
  disabled?: boolean;
  onSend: () => void;
  onAttach: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

interface AttachmentChipsProps {
  files: File[];
  onRemove: (_index: number) => void;
}

const AttachmentChips = ({ files, onRemove }: AttachmentChipsProps) => {
  if (files.length === 0) return null;
  return (
    <Flex wrap="wrap" gap="6px" mb="8px">
      {files.map((file, idx) => (
        <Flex
          key={`${file.name}-${idx}`}
          align="center"
          gap="6px"
          bg="secondary-50"
          border="1px solid"
          borderColor="secondary-100"
          borderRadius="8px"
          px="8px"
          py="4px"
          maxW="240px"
        >
          <Paperclip size={12} color="currentColor" />
          <Text
            variant="small"
            truncate
            className="text-secondary-800 font-[450]"
          >
            {file.name}
          </Text>
          <Text variant="small" className="text-secondary-400 shrink-0">
            {formatBytes(file.size)}
          </Text>
          <Box
            as="button"
            type="button"
            aria-label={`Remove ${file.name}`}
            onClick={() => onRemove(idx)}
            bg="transparent"
            color="secondary-500"
            cursor="pointer"
            display="inline-flex"
            ml="2px"
            _hover={{ color: 'secondary-700' }}
          >
            <XClose size={12} color="currentColor" />
          </Box>
        </Flex>
      ))}
    </Flex>
  );
};

function ComposerActions({
  canSend,
  disabled = false,
  onSend,
  onAttach,
}: ActionsProps): JSX.Element {
  return (
    <Box display="flex" alignItems="center" gap="4px">
      <Button
        size="icon"
        variant="quaternary"
        aria-label="Attach file"
        className="text-secondary-700"
        disabled={disabled}
        onClick={onAttach}
      >
        <Paperclip size={16} color="currentColor" />
      </Button>
      <Button
        size="icon"
        aria-label="Send"
        disabled={!canSend || disabled}
        onClick={onSend}
        className="bg-secondary-900 text-base-white disabled:bg-secondary-200 disabled:text-secondary-400 hover:bg-secondary-900"
      >
        <Send01 size={16} color="currentColor" />
      </Button>
    </Box>
  );
}

export const Composer = ({
  placeholder = 'Ask Kula...',
  multiline = false,
  isAwaiting = false,
  autoFocus = false,
  onSubmit,
  onKeyType,
}: ComposerProps) => {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [plainText, setPlainText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const trimmed = plainText.trim();
  // Allow send when there's text OR attachments — uploading just a file
  // (no prompt) is a valid intent the caller can interpret.
  const canSend = trimmed.length > 0 || files.length > 0;

  const handleSend = useCallback(() => {
    if (!canSend || isAwaiting) return;
    const serialized = editorRef.current?.getSerialized().trim() ?? '';
    const attached = files;
    editorRef.current?.clear();
    setPlainText('');
    setFiles([]);
    onSubmit?.(serialized, attached);
  }, [canSend, files, isAwaiting, onSubmit]);

  // Submit on Enter; Shift+Enter inserts a newline (multiline only).
  // While awaiting a reply, Enter is swallowed so the caller's
  // one-in-flight contract isn't broken by a quick second keystroke.
  //
  // The external `onKeyType` runs first so it can `preventDefault` to
  // suppress the built-in submit-on-Enter when the caller wants to
  // intercept the keystroke.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      onKeyType?.(event);
      if (event.defaultPrevented) return;
      if (event.key !== 'Enter') return;
      if (multiline && event.shiftKey) return;
      event.preventDefault();
      if (isAwaiting) return;
      handleSend();
    },
    [handleSend, isAwaiting, multiline, onKeyType]
  );

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Drop oversized files quietly — the chip would mislead users about
    // what's actually being sent.
    const valid = picked.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    setFiles((prev) => [...prev, ...valid]);
    // Reset so the same file can be re-picked after removal.
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      hidden
      onChange={handleFilesPicked}
      data-test-id="composer-file-input"
    />
  );

  if (multiline) {
    return (
      <Box>
        <Box
          data-mention-input-container
          bg="base-white"
          border="1px solid"
          borderColor="secondary-100"
          borderRadius="12px"
          px="10px"
          py="12px"
        >
          <AttachmentChips files={files} onRemove={handleRemoveFile} />
          <MentionEditor
            ref={editorRef}
            multiline
            autoFocus={autoFocus}
            placeholder={placeholder}
            onChange={setPlainText}
            onKeyDown={handleKeyDown}
            minHeight={56}
            maxHeight={160}
            testId="composer-input"
          />
          <Box display="flex" justifyContent="flex-end" mt="6px">
            <ComposerActions
              canSend={canSend}
              disabled={isAwaiting}
              onSend={handleSend}
              onAttach={handleAttachClick}
            />
          </Box>
        </Box>
        {hiddenFileInput}
        <Text className="text-secondary-400 text-center mt-2 !text-[12px] font-[450]">
          {DISCLAIMER}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <AttachmentChips files={files} onRemove={handleRemoveFile} />
      <Flex
        data-mention-input-container
        align="center"
        gap="8px"
        bg="base-white"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="12px"
        px="12px"
        py="8px"
      >
        <Box flex="1" minW="0">
          <MentionEditor
            ref={editorRef}
            autoFocus={autoFocus}
            placeholder={placeholder}
            onChange={setPlainText}
            onKeyDown={handleKeyDown}
            minHeight={24}
            testId="composer-input"
          />
        </Box>
        <ComposerActions
          canSend={canSend}
          disabled={isAwaiting}
          onSend={handleSend}
          onAttach={handleAttachClick}
        />
      </Flex>
      {hiddenFileInput}
      <Text className="text-center mt-2 text-secondary-400 font-[450] text-[12px]">
        {DISCLAIMER}
      </Text>
    </Box>
  );
};
